<template>
  <td :class="['fc-cell', { qs: isQuarterStart }]">
    <template v-if="type === 'opportunity' || isRecurringType">
      <div v-if="invoices.length >= 1" class="cell-single cell-multi-wrap">
        <span
          class="cell-bar"
          :class="invoices.length === 1 ? barClass(invoices[0]) : multiBarClass"
          @mouseenter="showPopover = true"
          @mouseleave="scheduleHide"
          @click="invoices.length === 1 ? onChipClick(invoices[0]) : null"
        >
          {{ formatCompact(invoicesTotal) }}
          <span v-if="invoices.length > 1" class="multi-badge">{{ invoices.length }}</span>
        </span>
        <div
          v-if="showPopover"
          class="cell-popover"
          @mouseenter="cancelHide"
          @mouseleave="scheduleHide"
        >
          <div
            v-for="(inv, idx) in invoices"
            :key="idx"
            class="popover-row"
            :class="{ clickable: !!inv.invoice_id }"
            @click="onChipClick(inv)"
          >
            <span class="popover-dot" :class="barClass(inv)"></span>
            <span class="popover-label">{{ inv.invoice_number ? inv.invoice_number : ('Instalment' + (inv.instalment_number ? ' #' + inv.instalment_number : '')) }}</span>
            <span v-if="inv.invoice_number && inv.instalment_number" class="popover-ref">Inst #{{ inv.instalment_number }}</span>
            <span class="popover-amount">${{ inv.amount / 100 | currency }}</span>
            <span v-if="inv.invoice_status" class="popover-status">{{ inv.invoice_status }}</span>
          </div>
        </div>
      </div>
      <div v-else-if="isRecurringType && monthData" class="cell-single">
        <span class="cell-bar bar-recurring">
          {{ formatCompact(monthData.amount) }}
        </span>
      </div>
    </template>
    <template v-else-if="type === 'tentative'">
      <div v-if="!editing && monthData" class="cell-single">
        <span class="cell-bar bar-tentative" @click="startEdit">
          {{ formatCompact(monthData.amount) }}
        </span>
      </div>
      <div v-else-if="editing" class="cell-edit" @mousedown.self.prevent>
        <input
          ref="editInput"
          v-model="editValue"
          type="number"
          step="0.01"
          class="edit-input"
          @blur="saveEdit"
          @keyup.enter="saveEdit"
          @keyup.escape="cancelEdit"
        />
      </div>
      <div v-else class="cell-empty" @click="startEdit">
        &nbsp;
      </div>
    </template>
  </td>
</template>

<script>
export default {
  props: {
    type: { type: String, required: true },
    invoices: { type: Array, default: () => [] },
    monthData: { type: Object, default: null },
    month: { type: String, required: true },
    rowStatus: { type: String, default: '' },
    isQuarterStart: { type: Boolean, default: false },
    compact: { type: Boolean, default: true }
  },

  data() {
    return {
      editing: false,
      editValue: '',
      editHadValue: false,
      showPopover: false,
      hideTimer: null
    }
  },

  computed: {
    isRecurringType() {
      return ['sla', 'retainer', 'budget', 'devops', 'hosting', 'unknown'].includes(this.type)
    },

    invoicesTotal() {
      return this.invoices.reduce((sum, inv) => sum + inv.amount, 0)
    },

    multiBarClass() {
      const invoices = this.invoices
      const allPaid = invoices.every(inv => inv.invoice_status === 'PAID')
      if (allPaid) return 'bar-paid'
      const allDraftOrSubmitted = invoices.every(inv => inv.invoice_status === 'DRAFT' || inv.invoice_status === 'SUBMITTED')
      if (allDraftOrSubmitted) return 'bar-signed'
      const anyInvoiced = invoices.some(inv => inv.invoice_id || (inv.invoice_status && inv.invoice_status !== 'DRAFT' && inv.invoice_status !== 'SUBMITTED'))
      if (anyInvoiced) return 'bar-raised'
      const anyPredicted = invoices.some(inv => inv.predicted)
      if (anyPredicted) return 'bar-quoted'
      return 'bar-signed'
    }
  },

  methods: {
    formatCompact(cents) {
      const sign = cents < 0 ? '-' : ''
      if (this.compact) {
        const dollars = Math.abs(cents / 100)
        if (dollars >= 1000) return sign + '$' + Math.round(dollars / 1000) + 'K'
        return sign + '$' + dollars.toLocaleString()
      }
      const dollars = Math.abs(cents / 100)
      return sign + '$' + this.$options.filters.currency(dollars)
    },

    formatFull(cents) {
      const dollars = cents / 100
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(dollars)
    },

    isPaid(inv) {
      return inv.invoice_status === 'PAID'
    },

    barClass(inv) {
      if (this.isPaid(inv)) return 'bar-paid'
      if (inv.invoice_status === 'DRAFT' || inv.invoice_status === 'SUBMITTED') return 'bar-signed'
      if (inv.invoice_id || inv.invoice_status) return 'bar-raised'
      if (inv.predicted) return 'bar-quoted'
      return 'bar-signed'
    },

    onChipClick(inv) {
      if (inv.invoice_id) {
        this.$router.push({ name: 'invoices.show', params: { invoice_id: inv.invoice_id } })
      } else if (inv.instalment_id) {
        this.$emit('navigate-opportunity')
      }
    },

    scheduleHide() {
      this.hideTimer = setTimeout(() => { this.showPopover = false }, 150)
    },

    cancelHide() {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer)
        this.hideTimer = null
      }
    },

    startEdit() {
      this.editing = true
      this.editHadValue = !!this.monthData
      this.editValue = this.monthData ? (this.monthData.amount / 100).toFixed(2) : ''
      this.$nextTick(() => {
        if (this.$refs.editInput) this.$refs.editInput.focus()
      })
    },

    saveEdit() {
      // Guard against double-fire (enter keyup followed by blur) and against
      // the blur that lands after a save has already been committed.
      if (!this.editing) return
      this.editing = false
      const val = parseFloat(this.editValue)
      if (isNaN(val) || val === 0) {
        // Only treat a cleared input as a delete when the editor was opened
        // on an existing value — a stale editor opened before the previous
        // save landed must not wipe it.
        if (this.monthData && this.editHadValue) {
          this.$emit('remove-month', this.monthData.id)
        }
        return
      }
      this.$emit('update-month', { month: this.month, amount: val.toFixed(2) })
    },

    cancelEdit() {
      this.editing = false
    }
  },

  beforeDestroy() {
    if (this.hideTimer) clearTimeout(this.hideTimer)
  }
}
</script>

<style scoped>
.fc-cell {
  padding: 0;
  text-align: center;
  vertical-align: middle;
  min-width: 80px;
  position: relative;
}
.qs {
  border-left: 2px solid #e2e5f0 !important;
}

.cell-single {
  padding: 4px 2px;
}

/* Cell bars */
.cell-bar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  min-width: 56px;
  border-radius: 4px;
  color: #fff;
  font-weight: 600;
  font-size: 0.72rem;
  padding: 0 10px;
  white-space: nowrap;
  letter-spacing: -0.2px;
  cursor: pointer;
  transition: opacity 0.15s;
  position: relative;
}
.cell-bar:hover {
  opacity: 0.85;
}

/* Multi-invoice badge */
.multi-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.35);
  font-size: 0.58rem;
  font-weight: 700;
  margin-left: 4px;
  line-height: 1;
}

/* Colour palette — based on invoice status */
.bar-raised { background: #34c38f; }
.bar-paid { background: #2ba97a; }
.bar-signed { background: #3b82f6; }
.bar-recurring { background: #8b5cf6; }
.bar-quoted { background: #e0973a; }
.bar-tentative { background: #c5cae0; color: #5a5f7a; }

/* Multi-invoice popover */
.cell-multi-wrap {
  position: relative;
}
.cell-popover {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  background: #fff;
  border: 1px solid #e2e5f0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  padding: 6px 0;
  min-width: 200px;
  white-space: nowrap;
}
.popover-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  font-size: 0.72rem;
  color: #1e1e2d;
  transition: background 0.1s;
}
.popover-row.clickable {
  cursor: pointer;
}
.popover-row.clickable:hover {
  background: #f5f6fa;
}
.popover-dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}
.popover-label {
  font-weight: 600;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}
.popover-ref {
  font-size: 0.62rem;
  color: #a0a4b8;
  font-weight: 500;
}
.popover-amount {
  font-weight: 700;
  color: #1e1e2d;
}
.popover-status {
  font-size: 0.62rem;
  color: #8b8fa7;
  font-weight: 500;
}

/* Empty tentative cell */
.cell-empty {
  min-height: 28px;
  cursor: pointer;
}

/* Edit input */
.cell-edit {
  padding: 4px 2px;
}
.edit-input {
  width: 72px;
  font-size: 0.75rem;
  padding: 3px 6px;
  border: 1px solid #4057df;
  border-radius: 4px;
  outline: none;
  text-align: center;
}
</style>
